import { Suspense, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { ConstructionLoader } from '@/components/ui/ConstructionLoader';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { useAuth } from '@/features/auth/AuthContext';
import { ModuleTabs } from './ModuleTabs';
import { NavRail } from './NavRail';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { useSidebar } from './useSidebar';

import type { ProjectOption } from './ProjectSelector';

/*
  The review layer — the pinned comments and the Development board — is off.
  It was how the client and the build team talked about OneParacon itself, and
  the client asked for it gone from the product on 30 Sep 2026. The routes and
  the comments they left are still in the repository and the database; nothing
  reaches them from the interface any more.
*/

/**
 * Application chrome.
 *
 * Responsive behaviour:
 *  - `>= lg` : fixed rail (72px) or fixed sidebar (288px); content is offset by
 *              the matching padding so nothing sits underneath it.
 *  - `< lg`  : both are off-canvas. The topbar's hamburger opens the sidebar as
 *              a drawer over a backdrop.
 */
export function AppShell({
  projects,
  currentProjectId,
  onProjectChange,
}: {
  projects: ProjectOption[];
  currentProjectId: string;
  onProjectChange: (id: string) => void;
}) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const sidebar = useSidebar();
  const [signingOut, setSigningOut] = useState(false);

  if (!user) return null;

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    await signOut();
    navigate('/login', { replace: true });
  };

  const sidebarUser = { name: user.full_name, roleLabel: user.role_label, avatarUrl: user.avatar_url };

  return (
    <div className="min-h-screen bg-cloud">
      {/* Desktop navigation */}
      <div className="hidden lg:block">
        {sidebar.collapsed ? (
          <NavRail
            onExpand={() => sidebar.setCollapsed(false)}
            userName={user.full_name}
            avatarUrl={user.avatar_url}
            projects={projects}
            currentProjectId={currentProjectId}
            onProjectChange={onProjectChange}
          />
        ) : (
          <Sidebar
            onCollapse={() => sidebar.setCollapsed(true)}
            user={sidebarUser}
            projects={projects}
            currentProjectId={currentProjectId}
            onProjectChange={onProjectChange}
          />
        )}
      </div>

      {/* Mobile drawer */}
      {sidebar.mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            aria-hidden
            onClick={sidebar.closeMobile}
            className="absolute inset-0 animate-fade-in bg-midnight/50"
          />
          <div className="absolute inset-y-0 left-0 w-sidebar max-w-[85vw] animate-flyout-in shadow-flyout">
            <Sidebar
              variant="drawer"
              onClose={sidebar.closeMobile}
              user={sidebarUser}
              projects={projects}
              currentProjectId={currentProjectId}
              onProjectChange={onProjectChange}
            />
          </div>
        </div>
      )}

      <div
        className={cn(
          'flex min-h-screen flex-col transition-[padding] duration-200',
          sidebar.collapsed ? 'lg:pl-rail' : 'lg:pl-sidebar',
        )}
      >
        {/* The top bar and the module's tabs stay pinned together while the page scrolls. */}
        <div className="sticky top-0 z-20">
          <Topbar
            user={user}
            onOpenMobileNav={sidebar.openMobile}
            onSignOut={handleSignOut}
          />
          <ModuleTabs />
        </div>
        <main className="flex-1">
          {/* A screen still downloading keeps the shell on screen around the loader. */}
          {/*
            A screen that throws should say so, not go blank. React unmounts
            the subtree on a render error, and a blank page tells nobody
            whether to wait, reload or report it.
          */}
          <ErrorBoundary>
            <Suspense fallback={<ConstructionLoader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

    </div>
  );
}
